
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/hooks/use-toast';

export function useOnboarding() {
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    async function checkOnboardingStatus() {
      if (!user) {
        console.log("[useOnboarding] No user, setting loading to false");
        setLoading(false);
        return;
      }

      // Removed excessive onboarding status checking logs

      try {
        const { data, error } = await supabase
          .from('user_settings')
          .select('completed_onboarding')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error("[useOnboarding] Error fetching onboarding status:", error);
          throw error;
        }

        // Removed excessive onboarding data logging
        setHasCompletedOnboarding(data?.completed_onboarding || false);
      } catch (error) {
        console.error('Error fetching onboarding status:', error);
      } finally {
        console.log("[useOnboarding] Setting loading to false");
        setLoading(false);
      }
    }

    checkOnboardingStatus();
  }, [user]);

  const markOnboardingComplete = async (onboardingData: any) => {
    if (!user) {
      console.error("❌ [useOnboarding] Cannot complete onboarding: No authenticated user found");
      return;
    }

    try {
      console.log("🔄 [useOnboarding] Updating onboarding status for user:", user.id);
      console.log("🔄 [useOnboarding] Onboarding data received:", onboardingData);
      
      // First check if settings record exists
      const { data: existingSettings } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id);

      console.log("🔄 [useOnboarding] Existing user settings:", existingSettings);

      // If no record exists yet, insert it
      if (!existingSettings || existingSettings.length === 0) {
        console.log("🔄 [useOnboarding] No existing settings, inserting new record...");
        const { error: insertError } = await supabase
          .from('user_settings')
          .insert({ 
            user_id: user.id,
            completed_onboarding: true 
          });

        if (insertError) {
          console.error("❌ [useOnboarding] Settings insert error:", insertError);
          throw insertError;
        }
        console.log("✅ [useOnboarding] User settings inserted successfully");
      } else {
        // Otherwise update existing record
        console.log("🔄 [useOnboarding] Updating existing settings record...");
        const { error: updateError } = await supabase
          .from('user_settings')
          .update({ completed_onboarding: true })
          .eq('user_id', user.id);

        if (updateError) {
          console.error("❌ [useOnboarding] Settings update error:", updateError);
          throw updateError;
        }
        console.log("✅ [useOnboarding] User settings updated successfully");
      }
      
      console.log("✅ [useOnboarding] Successfully updated user_settings");

      // Then update the profiles table with the onboarding data
      console.log("🔄 [useOnboarding] Updating profile with energy_profile data...");
      console.log("🔄 [useOnboarding] Energy profile data to save:", onboardingData);
      
      const { data: profileUpdateData, error: profileError } = await supabase
        .from('profiles')
        .update({ 
          energy_profile: onboardingData
        })
        .eq('id', user.id)
        .select(); // Add select to see what was updated

      if (profileError) {
        console.error("❌ [useOnboarding] Profile update error:", profileError);
        throw profileError;
      }
      
      console.log("✅ [useOnboarding] Successfully updated user profile:", profileUpdateData);
      
      // Save timezone to availability_settings if provided
      if (onboardingData.timezone) {
        console.log("🕰️ [useOnboarding] Saving timezone to availability_settings:", onboardingData.timezone);
        
        // Check if availability_settings record exists
        const { data: existingAvailability } = await supabase
          .from('availability_settings')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (existingAvailability) {
          // Update existing record
          const { error: timezoneError } = await supabase
            .from('availability_settings')
            .update({ timezone: onboardingData.timezone })
            .eq('user_id', user.id);
          
          if (timezoneError) {
            console.error("❌ [useOnboarding] Failed to update timezone:", timezoneError);
            // Don't throw - timezone is not critical enough to fail onboarding
          } else {
            console.log("✅ [useOnboarding] Timezone saved successfully");
          }
        } else {
          // Insert new record with timezone
          const { error: timezoneError } = await supabase
            .from('availability_settings')
            .insert({
              user_id: user.id,
              timezone: onboardingData.timezone,
              buffer_before: 15,
              buffer_after: 15,
              min_notice_time: 60,
              country_code: 'US'
            });
          
          if (timezoneError) {
            console.error("❌ [useOnboarding] Failed to insert timezone:", timezoneError);
            // Don't throw - timezone is not critical enough to fail onboarding
          } else {
            console.log("✅ [useOnboarding] Timezone saved successfully");
          }
        }
      }
      
      // Update local state to reflect the change
      setHasCompletedOnboarding(true);
      
      // Trigger a small delay to ensure the state update is processed
      setTimeout(() => {
        console.log("🔄 [useOnboarding] Triggering navigation after onboarding completion");
        // The Navigation component will handle the redirect
      }, 100);
      
      return true;
    } catch (error) {
      console.error('❌ [useOnboarding] Error updating onboarding status:', error);
      throw error;
    }
  };

  const refreshOnboardingStatus = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('completed_onboarding')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error("[useOnboarding] Error refreshing onboarding status:", error);
        return;
      }

      console.log("[useOnboarding] Refreshed onboarding status:", data);
      setHasCompletedOnboarding(data?.completed_onboarding || false);
    } catch (error) {
      console.error('Error refreshing onboarding status:', error);
    }
  };

  return { hasCompletedOnboarding, loading, markOnboardingComplete, refreshOnboardingStatus };
}
